import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { HttpErrorResponse } from '@angular/common/http';

/**
 * Service to handle HTTP errors with user-friendly messages.
 * Maps HTTP status codes to translated error messages.
 */
@Injectable({
  providedIn: 'root',
})
export class ApiErrorHandlerService {
  constructor(
    private readonly _toaster: ToastrService,
    private readonly _translator: TranslateService,
  ) {}

  public handleError(error: number | HttpErrorResponse) {
    if (error === null || error === undefined) {
      this._translator
        .get('api.error.unknown_error_occurred')
        .subscribe((translated: string) => {
          this._toaster.error(translated);
        });
      return;
    }
    const status = typeof error === 'number' ? error : error.status;
    let message: string | undefined;

    // Handle structured validation/error responses from backend
    if (typeof error !== 'number' && error) {
      const apiError = error.error;
      if (apiError) {
        if (
          Array.isArray(apiError.violations) &&
          apiError.violations.length > 0
        ) {
          apiError.violations.forEach((violation: any) => {
            if (violation.message) {
              this._toaster.error(violation.message);
            }
          });
          return;
        } else if (apiError.detail || apiError.description) {
          const detail = apiError.detail || apiError.description;
          // Check for database foreign key constraint violation (RESTRICT / SQLSTATE[23503])
          if (
            detail.includes('23503') ||
            detail.toLowerCase().includes('foreign key')
          ) {
            this._translator
              .get('api.error.database_restrict')
              .subscribe((translated: string) => {
                this._toaster.error(translated);
              });
            return;
          }

          this._toaster.error(detail);
          return;
        }
      }
    }

    switch (status) {
      case 401:
        this._translator
          .get(`api.error.reconnection`)
          .subscribe((translated: string) => {
            this._toaster.info(translated);
          });
        break;
      case 400:
      case 403:
      case 404:
      case 405:
        // These codes are intentionally silent if backend provides specific error messages via API response
        break;
      case 422:
        message = 'validation_error';
        break;
      case 500:
      case 501:
      case 502:
      case 503:
      case 504:
        message = 'server_error';
        break;
      default:
        message = 'unknown_error_occurred';
    }

    if (!message) return;

    this._translator
      .get(`api.error.${message}`)
      .subscribe((translated: string) => {
        this._toaster.error(translated);
      });
  }
}
